import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '@/App.css';
import axios from 'axios';
import { Loader2, ThumbsUp, ThumbsDown, Upload, Send, MessageCircle, ExternalLink, LogIn, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import API, { getBackendUrl } from '@/lib/api';

/**
 * Markdown-light parser: Converts plaintext to array of block objects
 * Supports: **bold**, bullet lists (- ), numbered lists (1. ), headings (**Title:**)
 * No HTML in input - this is pure text parsing
 */
function parseMarkdownLight(text) {
  if (!text) return [];
  
  const lines = text.split('\n');
  const blocks = [];
  let i = 0;
  
  while (i < lines.length) {
    const line = lines[i];
    const trimmedLine = line.trim();
    
    // Empty line: skip
    if (trimmedLine === '') {
      i++;
      continue;
    }
    
    // Check for heading: exactly **...:** pattern
    const headingMatch = trimmedLine.match(/^\*\*(.+?):\*\*$/);
    if (headingMatch) {
      blocks.push({ type: 'heading', content: headingMatch[1] + ':' });
      i++;
      continue;
    }
    
    // Check for bullet list: consecutive lines starting with "- "
    if (trimmedLine.startsWith('- ')) {
      const items = [];
      while (i < lines.length && lines[i].trim().startsWith('- ')) {
        items.push(lines[i].trim().substring(2));
        i++;
      }
      blocks.push({ type: 'ul', items: items });
      continue;
    }
    
    // Check for ordered list: consecutive lines matching ^\d+\.\s+
    const orderedMatch = trimmedLine.match(/^(\d+)\.\s+(.*)$/);
    if (orderedMatch) {
      const items = [];
      while (i < lines.length) {
        const olMatch = lines[i].trim().match(/^\d+\.\s+(.*)$/);
        if (olMatch) {
          items.push(olMatch[1]);
          i++;
        } else {
          break;
        }
      }
      blocks.push({ type: 'ol', items: items });
      continue;
    }
    
    // Regular paragraph: combine consecutive non-empty, non-special lines
    const paragraphLines = [];
    while (i < lines.length) {
      const pLine = lines[i].trim();
      if (pLine === '') break;
      // Stop if next line starts a new block type
      if (pLine.match(/^\*\*.+?:\*\*$/)) break;
      if (pLine.startsWith('- ')) break;
      if (pLine.match(/^\d+\.\s+/)) break;
      paragraphLines.push(pLine);
      i++;
    }
    if (paragraphLines.length > 0) {
      blocks.push({ type: 'p', content: paragraphLines.join(' ') });
    }
  }
  
  return blocks;
}

/**
 * Apply inline bold: Split text by ** and return array of React elements
 * If unmatched ** occurs, return plain text (no bold)
 */
function renderInlineBold(text, keyPrefix = '') {
  if (!text) return null;
  
  const parts = text.split('**');
  
  // If only 1 part, no ** found - return plain text
  if (parts.length === 1) {
    return text;
  }
  
  // If even number of parts (odd number of **), we have unmatched **
  // Treat as plain text (no bold)
  if (parts.length % 2 === 0) {
    return text;
  }
  
  // Odd number of parts (even number of **) - apply bold
  return parts.map((part, index) => {
    if (index % 2 === 0) {
      // Even index: regular text
      return part || null;
    } else {
      // Odd index: bold text
      return <strong key={`${keyPrefix}-bold-${index}`}>{part}</strong>;
    }
  }).filter(Boolean);
}

/**
 * Render parsed markdown blocks as React elements
 * No dangerouslySetInnerHTML - pure JSX rendering
 */
function MarkdownLightRenderer({ text }) {
  if (!text) return null;
  
  const blocks = parseMarkdownLight(text);
  
  if (blocks.length === 0) {
    return <p>{text}</p>;
  }
  
  return (
    <>
      {blocks.map((block, blockIndex) => {
        const key = `block-${blockIndex}`;
        
        switch (block.type) {
          case 'heading':
            return (
              <p key={key} className="md-heading">
                <strong>{renderInlineBold(block.content, key)}</strong>
              </p>
            );
          case 'ul':
            return (
              <ul key={key} className="md-list">
                {block.items.map((item, itemIndex) => (
                  <li key={`${key}-item-${itemIndex}`}>
                    {renderInlineBold(item, `${key}-item-${itemIndex}`)}
                  </li>
                ))}
              </ul>
            );
          case 'ol':
            return (
              <ol key={key} className="md-list">
                {block.items.map((item, itemIndex) => (
                  <li key={`${key}-item-${itemIndex}`}>
                    {renderInlineBold(item, `${key}-item-${itemIndex}`)}
                  </li>
                ))}
              </ol>
            );
          case 'p':
          default:
            return (
              <p key={key}>
                {renderInlineBold(block.content, key)}
              </p>
            );
        }
      })}
    </>
  );
}

function PublicChat() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [municipalityId, setMunicipalityId] = useState(null);
  const [municipalityIdError, setMunicipalityIdError] = useState(false);
  // Clarification context for follow-up handling
  const [pendingClarification, setPendingClarification] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Read municipality_id from URL query param or env var only (no hardcoded default)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlMunicipalityId = urlParams.get('municipality_id');
    
    if (urlMunicipalityId) {
      setMunicipalityId(urlMunicipalityId);
      setMunicipalityIdError(false);
    } else {
      // Fallback to env var only - no hardcoded default
      const envMunicipalityId = process.env.REACT_APP_DEFAULT_MUNICIPALITY_ID;
      if (envMunicipalityId) {
        setMunicipalityId(envMunicipalityId);
        setMunicipalityIdError(false);
      } else {
        // No municipality_id available - show error (already handled by existing UI)
        setMunicipalityId(null);
        setMunicipalityIdError(true);
      }
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    // Block sending if municipality_id is missing
    if (!municipalityId) {
      setMunicipalityIdError(true);
      toast.error('municipality_id fehlt');
      return;
    }

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    const requestUrl = `${API}/ask`;
    const requestPayload = {
      message: input,  // Use 'message' instead of 'question' for consistency
      municipality_id: municipalityId  // Legacy fallback (authenticated users can still use this)
    };
    
    // Include clarification context if we have a pending clarification
    if (pendingClarification) {
      requestPayload.clarification_context = {
        original_user_question: pendingClarification.original_user_question,
        assistant_clarification: pendingClarification.assistant_clarification || null
      };
    }
    
    // Clear pending clarification after including it in request
    setPendingClarification(null);
    
    // Debug logging
    if (process.env.NODE_ENV === 'development') {
      console.log('Making request to:', requestUrl);
      console.log('Payload:', requestPayload);
      console.log('API constant:', API);
      console.log('Current origin:', window.location.origin);
    }

    try {
      const response = await axios.post(requestUrl, requestPayload);

      const assistantMessage = {
        role: 'assistant',
        content: response.data.answer,
        citations: response.data.citations,
        confidence: response.data.confidence,
        followUps: response.data.follow_ups,
        disclaimer: response.data.disclaimer,
        portalLinks: response.data.portal_links || [],
        answerId: response.data.answer_id || null
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      // Store clarification context if response indicates pending clarification
      if (response.data.clarification && response.data.clarification.is_pending) {
        setPendingClarification({
          original_user_question: response.data.clarification.original_user_question,
          assistant_clarification: response.data.clarification.assistant_clarification
        });
      }
    } catch (error) {
      console.error('Error asking question:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        response: error.response,
        request: error.request,
        config: error.config
      });
      
      // Handle network errors (no response received)
      if (!error.response) {
        const isNetworkError = error.code === 'ERR_NETWORK' || 
                               error.message === 'Network Error' ||
                               error.message.includes('Failed to fetch');
        
        if (isNetworkError) {
          toast.error('Verbindungsfehler: Server nicht erreichbar. Bitte versuchen Sie es später erneut.');
          const errorMessage = {
            role: 'assistant',
            content: 'Es konnte keine Verbindung zum Server hergestellt werden. Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.',
            error: true
          };
          setMessages(prev => [...prev, errorMessage]);
          setLoading(false);
          return;
        }
      }
      
      // Handle 400 error for missing municipality_id
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('municipality_id')) {
        setMunicipalityIdError(true);
        toast.error('municipality_id fehlt');
      } else if (error.response?.status === 503) {
        toast.error('Service vorübergehend nicht verfügbar');
      } else if (error.response?.status === 429) {
        toast.error('Zu viele Anfragen. Bitte warten Sie einen Moment.');
      } else {
        const errorDetail = error.response?.data?.detail || error.message || 'Unbekannter Fehler';
        toast.error(`Fehler: ${errorDetail}`);
      }
      
      const errorMessage = {
        role: 'assistant',
        content: error.response?.data?.detail || 'Es tut mir leid, es gab einen Fehler bei der Verarbeitung Ihrer Frage. Bitte versuchen Sie es erneut.',
        error: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleFeedback = async (message, helpful) => {
    try {
      await axios.post(`${API}/feedback`, {
        answer_id: message.answerId || null,
        question: messages[messages.indexOf(message) - 1]?.content || '',
        helpful,
        reason: null,
        municipality_id: municipalityId || null
      });
      toast.success('Vielen Dank für Ihr Feedback!');
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', file.name);

    // Get auth token if available
    const token = localStorage.getItem('token');
    const headers = { 'Content-Type': 'multipart/form-data' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await axios.post(`${API}/docs/upload`, formData, { headers });
      
      const doc = response.data;
      const chunksMsg = doc.chunks_count > 0 ? `${doc.chunks_count} Abschnitte` : 'wird verarbeitet...';
      toast.success(`Dokument "${doc.title}" hochgeladen (${chunksMsg})`);
      setUploadDialogOpen(false);
    } catch (error) {
      console.error('Error uploading file:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Bitte melden Sie sich an, um Dokumente hochzuladen');
        navigate('/login');
      } else {
        toast.error(error.response?.data?.detail || 'Fehler beim Hochladen der Datei');
      }
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUrlFetch = async () => {
    if (!urlInput.trim()) return;

    setUploadingFile(true);
    const formData = new URLSearchParams();
    formData.append('url', urlInput.trim());

    // Get auth token if available
    const token = localStorage.getItem('token');
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await axios.post(`${API}/docs/fetch_url`, formData, { headers });
      
      const doc = response.data;
      const chunksMsg = doc.chunks_count > 0 ? `${doc.chunks_count} Abschnitte` : 'wird verarbeitet...';
      toast.success(`Dokument "${doc.title}" abgerufen (${chunksMsg})`);
      setUrlInput('');
      setUploadDialogOpen(false);
    } catch (error) {
      console.error('Error fetching URL:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Bitte melden Sie sich an, um URLs abzurufen');
        navigate('/login');
      } else {
        toast.error(error.response?.data?.detail || 'Fehler beim Abrufen der URL');
      }
    } finally {
      setUploadingFile(false);
    }
  };

  const getConfidenceBadge = (confidence) => {
    if (confidence >= 0.75) {
      return <span className="confidence-badge high" data-testid="confidence-badge">Hoch</span>;
    } else if (confidence >= 0.6) {
      return <span className="confidence-badge medium" data-testid="confidence-badge">Mittel</span>;
    } else {
      return <span className="confidence-badge low" data-testid="confidence-badge">Niedrig</span>;
    }
  };

  return (
    <div className="app-container" data-testid="app-container">
      <header className="header">
        <div className="header-content">
          <div className="header-title">
            <MessageCircle className="header-icon" />
            <div>
              <h1 data-testid="app-title">StadtKI</h1>
              {/* TODO(PR2): Display municipality name dynamically from API */}
              <p className="header-subtitle">Ihre Gemeinde</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <Button
              variant="outline"
              onClick={() => navigate('/login')}
              className="btn-glow"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Login
            </Button>
            <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="upload-button" data-testid="upload-trigger-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  Dokument hinzufügen
                </Button>
              </DialogTrigger>
            <DialogContent data-testid="upload-dialog">
              <DialogHeader>
                <DialogTitle>Dokument hinzufügen</DialogTitle>
                <DialogDescription>
                  Laden Sie ein Dokument hoch oder geben Sie eine URL Ihrer Gemeinde ein.
                </DialogDescription>
              </DialogHeader>
              <div className="upload-content">
                <div className="upload-section">
                  <label className="upload-label">Datei hochladen</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.html,.txt"
                    onChange={handleFileUpload}
                    disabled={uploadingFile}
                    className="file-input"
                    data-testid="file-input"
                  />
                  <p className="upload-hint">PDF, HTML oder Textdatei</p>
                </div>
                <div className="divider">ODER</div>
                <div className="upload-section">
                  <label className="upload-label">URL abrufen</label>
                  <div className="url-input-group">
                    <input
                      type="url"
                      value={urlInput}
                      onChange={(e) => setUrlInput(e.target.value)}
                      placeholder="https://www.gemeinde.de/..."
                      disabled={uploadingFile}
                      className="url-input"
                      data-testid="url-input"
                    />
                    <Button
                      onClick={handleUrlFetch}
                      disabled={!urlInput.trim() || uploadingFile}
                      data-testid="fetch-url-btn"
                    >
                      {uploadingFile ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Abrufen'}
                    </Button>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>
      </header>

      <div className="chat-container">
        {/* Error banner for missing municipality_id */}
        {municipalityIdError && (
          <Alert variant="destructive" className="m-4" data-testid="municipality-id-error">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>municipality_id fehlt</strong> - Bitte fügen Sie <code>?municipality_id=...</code> zur URL hinzu oder setzen Sie die Umgebungsvariable <code>REACT_APP_DEFAULT_MUNICIPALITY_ID</code>.
            </AlertDescription>
          </Alert>
        )}
        
        <div className="messages-container" data-testid="messages-container">
          {messages.length === 0 ? (
            <div className="welcome-screen">
              <MessageCircle className="welcome-icon" />
              <h2>Willkommen bei StadtKI</h2>
              <p>Stellen Sie Fragen zu Dokumenten und Informationen Ihrer Gemeinde.</p>
              <div className="example-questions">
                <p className="examples-title">Beispielfragen:</p>
                <button className="example-btn" onClick={() => setInput('Welchen Mindestabstand muss ein Carport zur Grundstücksgrenze einhalten?')}>
                  Mindestabstand für Carport?
                </button>
                <button className="example-btn" onClick={() => setInput('Welche Ruhezeiten gelten für Rasenmähen in Wohngebieten?')}>
                  Ruhezeiten für Rasenmähen?
                </button>
                <button className="example-btn" onClick={() => setInput('Wie beantrage ich einen Bewohnerparkausweis?')}>
                  Bewohnerparkausweis beantragen?
                </button>
              </div>
            </div>
          ) : (
            messages.map((message, index) => (
              <div
                key={index}
                className={`message ${message.role}`}
                data-testid={`message-${message.role}`}
              >
                {message.role === 'user' ? (
                  <div className="message-content user-message">
                    {message.content}
                  </div>
                )                 : (
                  <Card className="message-card">
                    <div className="message-content assistant-message">
                      <MarkdownLightRenderer text={message.content} />
                    </div>

                    {message.portalLinks && message.portalLinks.length > 0 && (
                      <div className="portal-links-container" data-testid="portal-links-container">
                        <h4 className="portal-links-title">
                          {message.portalLinks.some((portalLink) => portalLink?.kind === 'fallback')
                            ? 'Hier finden Sie eine Übersicht, wer für was zuständig ist („Was erledige ich wo?“):'
                            : message.portalLinks.some((portalLink) => portalLink?.link_type === 'event')
                            ? 'Mehr Infos finden Sie hier:'
                            : message.portalLinks.some((portalLink) => portalLink?.link_type === 'info')
                            ? 'Weitere Informationen:'
                            : 'Zur passenden Leistung im Bürgerportal:'}
                        </h4>
                        <div className="portal-links-list">
                          {message.portalLinks.map((portalLink, idx) => (
                            <a
                              key={idx}
                              href={portalLink.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="portal-link-item"
                              data-testid={`portal-link-${idx}`}
                            >
                              {portalLink.title}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {message.citations && message.citations.length > 0 && (
                      <div className="citations-badges" data-testid="citations-container">
                        <span className="citations-label">Quellen:</span>
                        {message.citations.map((citation, idx) => {
                          const href = citation.source_url || (citation.url?.startsWith('/api/') ? `${getBackendUrl()}${citation.url}` : citation.url);
                          return (
                            <a
                              key={idx}
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="cite-badge"
                              title={citation.title}
                              data-testid={`citation-${idx}`}
                            >
                              [{idx + 1}]
                            </a>
                          );
                        })}
                      </div>
                    )}

                    {message.confidence !== undefined && (
                      <div className="message-meta">
                        <div className="confidence-container">
                          Vertrauen: {getConfidenceBadge(message.confidence)}
                        </div>
                        <div className="feedback-buttons">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleFeedback(message, true)}
                            className="feedback-btn"
                            data-testid="feedback-helpful-btn"
                          >
                            <ThumbsUp className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleFeedback(message, false)}
                            className="feedback-btn"
                            data-testid="feedback-not-helpful-btn"
                          >
                            <ThumbsDown className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}

                    {message.followUps && message.followUps.length > 0 && (
                      <div className="follow-ups" data-testid="follow-ups">
                        <p className="follow-ups-title">Vorschläge:</p>
                        {message.followUps.map((followUp, idx) => (
                          <button
                            key={idx}
                            className="follow-up-btn"
                            onClick={() => setInput(followUp)}
                            data-testid={`follow-up-${idx}`}
                          >
                            {followUp}
                          </button>
                        ))}
                      </div>
                    )}

                    {message.disclaimer && (
                      <div className="disclaimer" data-testid="disclaimer">
                        {message.disclaimer}
                      </div>
                    )}
                  </Card>
                )}
              </div>
            ))
          )}
          {loading && (
            <div className="message assistant" data-testid="loading-indicator">
              <Card className="message-card loading-card">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Suche nach Antworten...</span>
              </Card>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="input-container">
          <form onSubmit={handleSubmit} className="input-form" data-testid="question-form">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Stellen Sie eine Frage zu Ihrer Gemeinde..."
              className="input-textarea"
              disabled={loading}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              data-testid="question-input"
            />
            <Button
              type="submit"
              disabled={!input.trim() || loading || !municipalityId}
              className="send-button"
              data-testid="send-question-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default PublicChat;

